import * as THREE from "three";

/**
 * The village's mood, derived from the clock rather than chosen.
 *
 * The four looks come from the voxel world's authored presets — dawn fog, day, sunset, night —
 * but they are interpolated across a UTC day instead of being switched between. Running on UTC
 * is deliberate: the whole guild is standing in the same village at the same hour, and someone
 * describing the sunset should be describing what everyone else can see.
 */
export type Mood = {
  night: boolean;
  sky: THREE.Color;
  fogDensity: number;
  sunColor: THREE.Color;
  sunIntensity: number;
  sunPosition: THREE.Vector3;
  ambientColor: THREE.Color;
  ambientIntensity: number;
  hemiSky: THREE.Color;
  hemiGround: THREE.Color;
  hemiIntensity: number;
  /** Drives lit windows and lanterns. */
  emissiveIntensity: number;
  /** Drives the warm point lights inside homes and on lamp posts. */
  lampIntensity: number;
};

type Keyframe = {
  hour: number;
  night: boolean;
  sky: number;
  fogDensity: number;
  sunColor: number;
  sunIntensity: number;
  sunPosition: [number, number, number];
  ambientColor: number;
  ambientIntensity: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  emissiveIntensity: number;
  lampIntensity: number;
};

/**
 * Fog densities are roughly a third of the authored slice's.
 *
 * Those values were tuned against a village about fifty units across, where the far bank of
 * the river was the furthest thing on screen. This world is several times that and still
 * growing, so the same densities buried everything past the first row of trees in haze. These
 * are set so the village reads clearly and only the outermost woodland softens.
 */
const KEYFRAMES: Keyframe[] = [
  {
    // Early morning mist. The thickest fog of the day, and the only time the far hills go.
    hour: 5,
    night: false,
    sky: 0xb0c4de,
    fogDensity: 0.006,
    sunColor: 0xddddee,
    sunIntensity: 0.8,
    sunPosition: [40, 35, 45],
    ambientColor: 0xc9d4e0,
    ambientIntensity: 0.6,
    hemiSky: 0xc2d0dc,
    hemiGround: 0x5a5f52,
    hemiIntensity: 0.65,
    emissiveIntensity: 3,
    lampIntensity: 2
  },
  {
    hour: 10,
    night: false,
    sky: 0x9cb37d,
    fogDensity: 0.0022,
    sunColor: 0xfff2d6,
    sunIntensity: 1.5,
    sunPosition: [55, 70, 40],
    ambientColor: 0xdbe6c8,
    ambientIntensity: 0.65,
    hemiSky: 0xa3c78b,
    hemiGround: 0x4d3823,
    hemiIntensity: 0.7,
    emissiveIntensity: 0.8,
    lampIntensity: 0
  },
  {
    // Golden hour. Low, hot and orange, with the lamps already coming up.
    hour: 19,
    night: false,
    sky: 0xfd7e14,
    fogDensity: 0.0028,
    sunColor: 0xffaa44,
    sunIntensity: 1.6,
    sunPosition: [60, 20, 20],
    ambientColor: 0xff8844,
    ambientIntensity: 0.4,
    hemiSky: 0xffa766,
    hemiGround: 0x4a2f1c,
    hemiIntensity: 0.5,
    emissiveIntensity: 4,
    lampIntensity: 3.5
  },
  {
    // Moonlit rather than black. A lot of the guild is looking at this during their own
    // afternoon, so it has to stay readable — the warmth comes from the windows, not the sky.
    hour: 22,
    night: true,
    sky: 0x090d16,
    fogDensity: 0.0034,
    sunColor: 0x6688cc,
    sunIntensity: 0.35,
    sunPosition: [-30, 40, -20],
    ambientColor: 0x223355,
    ambientIntensity: 0.15,
    hemiSky: 0x35507e,
    hemiGround: 0x14181f,
    hemiIntensity: 0.3,
    emissiveIntensity: 8,
    lampIntensity: 6.5
  }
];

/** UTC hour, with a `?hour=` override in development so a mood can be checked on demand. */
export function currentHour(): number {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("hour");
    if (override !== null && override !== "" && Number.isFinite(Number(override))) return Number(override);
  }
  const now = new Date();
  return now.getUTCHours() + now.getUTCMinutes() / 60;
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Interpolates the mood for a given hour, wrapping through midnight. */
export function moodAt(hour: number): Mood {
  let before = KEYFRAMES[KEYFRAMES.length - 1]!;
  let after = KEYFRAMES[0]!;
  for (let index = 0; index < KEYFRAMES.length; index += 1) {
    const current = KEYFRAMES[index]!;
    const next = KEYFRAMES[(index + 1) % KEYFRAMES.length]!;
    const spanEnd = next.hour > current.hour ? next.hour : next.hour + 24;
    const probe = hour >= current.hour ? hour : hour + 24;
    if (probe >= current.hour && probe <= spanEnd) {
      before = current;
      after = next;
      break;
    }
  }
  const spanEnd = after.hour > before.hour ? after.hour : after.hour + 24;
  const probe = hour >= before.hour ? hour : hour + 24;
  const t = THREE.MathUtils.clamp((probe - before.hour) / (spanEnd - before.hour || 1), 0, 1);

  return {
    night: t < 0.5 ? before.night : after.night,
    sky: new THREE.Color(before.sky).lerp(new THREE.Color(after.sky), t),
    fogDensity: mix(before.fogDensity, after.fogDensity, t),
    sunColor: new THREE.Color(before.sunColor).lerp(new THREE.Color(after.sunColor), t),
    sunIntensity: mix(before.sunIntensity, after.sunIntensity, t),
    sunPosition: new THREE.Vector3(...before.sunPosition).lerp(new THREE.Vector3(...after.sunPosition), t),
    ambientColor: new THREE.Color(before.ambientColor).lerp(new THREE.Color(after.ambientColor), t),
    ambientIntensity: mix(before.ambientIntensity, after.ambientIntensity, t),
    hemiSky: new THREE.Color(before.hemiSky).lerp(new THREE.Color(after.hemiSky), t),
    hemiGround: new THREE.Color(before.hemiGround).lerp(new THREE.Color(after.hemiGround), t),
    hemiIntensity: mix(before.hemiIntensity, after.hemiIntensity, t),
    emissiveIntensity: mix(before.emissiveIntensity, after.emissiveIntensity, t),
    lampIntensity: mix(before.lampIntensity, after.lampIntensity, t)
  };
}

export function currentMood(): Mood {
  return moodAt(currentHour());
}

/**
 * Crossfades two moods.
 *
 * The clock's own drift is slow enough to apply outright, but weather turns over in a step, and
 * a village that snaps from sunlit to overcast between two frames looks broken rather than
 * changeable. Weather is eased in over a few seconds with this.
 */
export function blendMood(from: Mood, to: Mood, t: number): Mood {
  const amount = THREE.MathUtils.clamp(t, 0, 1);
  return {
    night: amount < 0.5 ? from.night : to.night,
    sky: from.sky.clone().lerp(to.sky, amount),
    fogDensity: mix(from.fogDensity, to.fogDensity, amount),
    sunColor: from.sunColor.clone().lerp(to.sunColor, amount),
    sunIntensity: mix(from.sunIntensity, to.sunIntensity, amount),
    sunPosition: from.sunPosition.clone().lerp(to.sunPosition, amount),
    ambientColor: from.ambientColor.clone().lerp(to.ambientColor, amount),
    ambientIntensity: mix(from.ambientIntensity, to.ambientIntensity, amount),
    hemiSky: from.hemiSky.clone().lerp(to.hemiSky, amount),
    hemiGround: from.hemiGround.clone().lerp(to.hemiGround, amount),
    hemiIntensity: mix(from.hemiIntensity, to.hemiIntensity, amount),
    emissiveIntensity: mix(from.emissiveIntensity, to.emissiveIntensity, amount),
    lampIntensity: mix(from.lampIntensity, to.lampIntensity, amount)
  };
}
